import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  User, Mail, Lock, Phone, MapPin, Briefcase, 
  Upload, CheckCircle, ArrowRight, ArrowLeft, 
  Calendar, Hash, Globe, DollarSign, Camera,
  FileText, Shield, Eye, EyeOff, Users, AlertCircle,
  Plus, Trash2
} from 'lucide-react'
import { supabase } from '../../services/supabase'
import { signUp } from '../../services/authService'
import toast from 'react-hot-toast'

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' }
]

const Register = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showSSN, setShowSSN] = useState(false)
  
  const [formData, setFormData] = useState({
    accountType: 'personal',
    fullName: '',
    email: '',
    username: '',
    password: '',
    phone: '',
    dateOfBirth: '',
    ssn: '', // Full SSN
    citizenship: 'United States',
    addresses: [{
      streetAddress: '',
      city: '',
      state: '',
      zipCode: '',
      duration: '',
      isPrimary: true
    }],
    employmentStatus: '',
    employerName: '',
    jobTitle: '',
    annualIncome: '',
    incomeSource: '',
    documents: {
      driversLicenseFront: null,
      driversLicenseBack: null,
      additionalId: null,
      selfie: null
    },
    termsAccepted: false,
    privacyAccepted: false,
    esignConsent: false
  })

  const steps = [
    { number: 1, title: 'Account', shortTitle: 'Type', icon: User },
    { number: 2, title: 'Personal', shortTitle: 'Info', icon: User },
    { number: 3, title: 'Address', shortTitle: 'Address', icon: MapPin },
    { number: 4, title: 'Employment', shortTitle: 'Work', icon: Briefcase },
    { number: 5, title: 'Documents', shortTitle: 'Docs', icon: Upload },
    { number: 6, title: 'Review', shortTitle: 'Review', icon: CheckCircle }
  ]

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleSSNChange = (e) => {
    let value = e.target.value.replace(/\D/g, '') // Remove non-digits
    if (value.length > 9) value = value.slice(0, 9) // Max 9 digits
    
    // Format as XXX-XX-XXXX
    if (value.length >= 6) {
      value = `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`
    } else if (value.length >= 4) {
      value = `${value.slice(0, 3)}-${value.slice(3)}`
    }
    
    setFormData(prev => ({ ...prev, ssn: value }))
  }

  const handlePhoneChange = (e) => {
    let value = e.target.value.replace(/\D/g, '')
    if (value.length > 10) value = value.slice(0, 10)
    
    // Format as (XXX) XXX-XXXX
    if (value.length >= 7) {
      value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`
    } else if (value.length >= 4) {
      value = `(${value.slice(0, 3)}) ${value.slice(3)}`
    } else if (value.length >= 1) {
      value = `(${value}`
    }
    
    setFormData(prev => ({ ...prev, phone: value }))
  }

  const handleAddressChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.map((addr, i) => 
        i === index ? { ...addr, [field]: value } : addr
      )
    }))
  }

  const addAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [...prev.addresses, {
        streetAddress: '',
        city: '',
        state: '',
        zipCode: '',
        duration: '',
        isPrimary: false
      }]
    }))
  }

  const removeAddress = (index) => {
    if (formData.addresses.length === 1) {
      toast.error('You must have at least one address')
      return
    }
    setFormData(prev => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index)
    }))
  }

  const handleFileUpload = async (e, documentType) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5242880) {
      toast.error('File size must be less than 5MB')
      return
    }

    if (!['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'].includes(file.type)) {
      toast.error('Only JPG, PNG, or PDF files allowed')
      return
    }

    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [documentType]: file
      }
    }))
    
    toast.success('Document uploaded successfully')
  }

  const validateStep = (step) => {
    switch(step) {
      case 1:
        return formData.accountType !== ''
      case 2:
        const ssnDigits = formData.ssn.replace(/\D/g, '')
        return formData.fullName && formData.email && formData.username && 
               formData.password && formData.phone && formData.dateOfBirth && 
               ssnDigits.length === 9 && formData.password.length >= 8
      case 3:
        return formData.addresses.every(addr => 
          addr.streetAddress && addr.city && addr.state && addr.zipCode && addr.duration
        )
      case 4:
        return formData.employmentStatus && formData.annualIncome && formData.incomeSource
      case 5:
        return formData.documents.driversLicenseFront && 
               formData.documents.driversLicenseBack &&
               formData.documents.selfie
      case 6:
        return formData.termsAccepted && formData.privacyAccepted && formData.esignConsent
      default:
        return false
    }
  }

  const nextStep = () => {
    if (!validateStep(currentStep)) {
      toast.error('Please fill in all required fields')
      return
    }
    setCurrentStep(prev => Math.min(prev + 1, 6))
  }

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleSubmit = async () => {
    if (!validateStep(6)) {
      toast.error('Please accept all terms and conditions')
      return
    }

    setLoading(true)
    try {
      const primaryAddress = formData.addresses.find(a => a.isPrimary) || formData.addresses[0]
      const ssnDigits = formData.ssn.replace(/\D/g, '')
      const ssnLast4 = ssnDigits.slice(-4)
      
      const { data: authData, error: authError } = await signUp({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        fullName: formData.fullName,
        phone: formData.phone,
        address: primaryAddress.streetAddress
      })

      if (authError) throw new Error(authError)

      const userId = authData.user.id

      const { error: profileError } = await supabase
        .from('users')
        .update({
          account_type: formData.accountType,
          date_of_birth: formData.dateOfBirth,
          ssn_last_4: ssnLast4,
          citizenship: formData.citizenship,
          street_address: primaryAddress.streetAddress,
          city: primaryAddress.city,
          state: primaryAddress.state,
          zip_code: primaryAddress.zipCode,
          address_duration: primaryAddress.duration,
          employment_status: formData.employmentStatus,
          employer_name: formData.employerName,
          job_title: formData.jobTitle,
          annual_income: formData.annualIncome,
          income_source: formData.incomeSource,
          kyc_status: 'processing',
          kyc_submitted_at: new Date().toISOString()
        })
        .eq('id', userId)

      if (profileError) throw profileError

      const documentTypes = [
        { key: 'driversLicenseFront', type: 'drivers_license_front' },
        { key: 'driversLicenseBack', type: 'drivers_license_back' },
        { key: 'additionalId', type: 'state_id' },
        { key: 'selfie', type: 'selfie' }
      ]

      for (const doc of documentTypes) {
        const file = formData.documents[doc.key]
        if (!file) continue

        const fileExt = file.name.split('.').pop()
        const fileName = `${userId}/${doc.type}_${Date.now()}.${fileExt}`
        
        const { error: uploadError } = await supabase.storage
          .from('kyc-documents')
          .upload(fileName, file)

        if (uploadError) throw uploadError

        const { data: { publicUrl } } = supabase.storage
          .from('kyc-documents')
          .getPublicUrl(fileName)

        await supabase
          .from('kyc_documents')
          .insert({
            user_id: userId,
            document_type: doc.type,
            file_url: publicUrl,
            file_name: file.name,
            file_size: file.size,
            mime_type: file.type
          })
      }

      await supabase
        .from('kyc_history')
        .insert({
          user_id: userId,
          action: 'submitted',
          status: 'processing',
          notes: 'Initial KYC submission'
        })

      toast.success('Registration complete! Your account is being reviewed.')
      
      setTimeout(() => {
        navigate('/login')
      }, 2000)

    } catch (error) {
      console.error('Registration error:', error)
      toast.error(error.message || 'Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 py-2 sm:py-4 md:py-8 px-2 sm:px-4">
      <div className="w-full max-w-6xl mx-auto">
        {/* Progress Bar */}
        <div className="mb-3 sm:mb-6">
          <div className="flex items-center justify-between gap-1 overflow-x-auto pb-2 scrollbar-hide">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center min-w-[45px] sm:min-w-[70px]">
                  <div
                    className={`w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                      currentStep >= step.number
                        ? 'bg-blue-600 text-white scale-110'
                        : 'bg-gray-200 dark:bg-dark-700 text-gray-500'
                    }`}
                  >
                    {currentStep > step.number ? (
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                    ) : (
                      <step.icon className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                    )}
                  </div>
                  <span className={`text-[8px] sm:text-[10px] md:text-xs mt-1 font-medium text-center transition-colors whitespace-nowrap px-1 ${
                    currentStep >= step.number ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'
                  }`}>
                    <span className="hidden sm:inline">{step.title}</span>
                    <span className="sm:hidden">{step.shortTitle}</span>
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 h-0.5 sm:h-1 mx-0.5 sm:mx-1 rounded-full bg-gray-200 dark:bg-dark-700 overflow-hidden min-w-[15px] sm:min-w-[25px]">
                    <div
                      className="h-full bg-blue-600 transition-all duration-500"
                      style={{ width: currentStep > step.number ? '100%' : '0%' }}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white dark:bg-dark-800 rounded-lg shadow-lg p-3 sm:p-6 md:p-8 min-h-[400px]">
          <div className="animate-fadeIn">
            {/* Step 1: Account Type */}
            {currentStep === 1 && (
              <div className="space-y-3 sm:space-y-6">
                <div className="text-center mb-4 sm:mb-8">
                  <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                    Choose Account Type
                  </h2>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                    Select the type of account you want to open
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
                  {[
                    { value: 'personal', title: 'Personal', desc: 'Individual banking', icon: User },
                    { value: 'business', title: 'Business', desc: 'Business banking', icon: Briefcase },
                    { value: 'joint', title: 'Joint', desc: 'Shared account', icon: Users }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setFormData(prev => ({ ...prev, accountType: type.value }))}
                      className={`p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl border-2 transition-all hover:scale-105 active:scale-95 ${
                        formData.accountType === type.value
                          ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-dark-700 hover:border-blue-300'
                      }`}
                    >
                      <type.icon className={`w-6 h-6 sm:w-8 sm:h-8 md:w-12 md:h-12 mx-auto mb-2 sm:mb-4 ${
                        formData.accountType === type.value ? 'text-blue-600' : 'text-gray-400'
                      }`} />
                      <h3 className="font-semibold text-xs sm:text-sm md:text-base text-gray-900 dark:text-white mb-1">
                        {type.title}
                      </h3>
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400">
                        {type.desc}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Personal Information */}
            {currentStep === 2 && (
              <div className="space-y-3 sm:space-y-6">
                <div className="text-center mb-4 sm:mb-8">
                  <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                    Personal Information
                  </h2>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                    Tell us about yourself
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Full Legal Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      placeholder="(555) 000-0000"
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Username *
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="johndoe"
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Min. 8 characters"
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Date of Birth *
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Social Security Number *
                    </label>
                    <div className="relative">
                      <input
                        type={showSSN ? 'text' : 'password'}
                        name="ssn"
                        value={formData.ssn}
                        onChange={handleSSNChange}
                        placeholder="XXX-XX-XXXX"
                        maxLength="11"
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowSSN(!showSSN)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                      >
                        {showSSN ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
                      </button>
                    </div>
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">
                      9 digits • Encrypted and secure
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Address */}
            {currentStep === 3 && (
              <div className="space-y-3 sm:space-y-4">
                <div className="text-center mb-3 sm:mb-6">
                  <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                    Address Information
                  </h2>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                    Where do you live?
                  </p>
                </div>

                <div className="max-h-[350px] sm:max-h-[450px] overflow-y-auto space-y-3 sm:space-y-4 pr-1">
                  {formData.addresses.map((address, index) => (
                    <div key={index} className="border-2 border-gray-200 dark:border-dark-700 rounded-lg p-3 sm:p-4 space-y-3">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-xs sm:text-sm md:text-base text-gray-900 dark:text-white flex items-center gap-2">
                          <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                          Address {index + 1}
                          {address.isPrimary && (
                            <span className="px-2 py-0.5 text-[10px] sm:text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full">
                              Primary
                            </span>
                          )}
                        </h3>
                        {formData.addresses.length > 1 && (
                          <button
                            onClick={() => removeAddress(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Street Address *
                          </label>
                          <input
                            type="text"
                            value={address.streetAddress}
                            onChange={(e) => handleAddressChange(index, 'streetAddress', e.target.value)}
                            placeholder="123 Main Street"
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            City *
                          </label>
                          <input
                            type="text"
                            value={address.city}
                            onChange={(e) => handleAddressChange(index, 'city', e.target.value)}
                            placeholder="New York"
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            State *
                          </label>
                          <select
                            value={address.state}
                            onChange={(e) => handleAddressChange(index, 'state', e.target.value)}
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                            required
                          >
                            <option value="">Select State</option>
                            {US_STATES.map(state => (
                              <option key={state.code} value={state.code}>
                                {state.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            ZIP Code *
                          </label>
                          <input
                            type="text"
                            value={address.zipCode}
                            onChange={(e) => handleAddressChange(index, 'zipCode', e.target.value)}
                            placeholder="10001"
                            maxLength="5"
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Duration *
                          </label>
                          <select
                            value={address.duration}
                            onChange={(e) => handleAddressChange(index, 'duration', e.target.value)}
                            className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                            required
                          >
                            <option value="">Select duration</option>
                            <option value="less_than_1_year">Less than 1 year</option>
                            <option value="1_2_years">1-2 years</option>
                            <option value="2_5_years">2-5 years</option>
                            <option value="more_than_5_years">More than 5 years</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={addAddress}
                  className="w-full sm:w-auto px-4 py-2 text-sm sm:text-base border-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
                  Add Another Address
                </button>
              </div>
            )}

            {/* Step 4: Employment */}
            {currentStep === 4 && (
              <div className="space-y-3 sm:space-y-6">
                <div className="text-center mb-3 sm:mb-8">
                  <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                    Employment & Income
                  </h2>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                    Tell us about your employment
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Employment Status *
                    </label>
                    <select
                      name="employmentStatus"
                      value={formData.employmentStatus}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                      required
                    >
                      <option value="">Select status</option>
                      <option value="employed">Employed</option>
                      <option value="self_employed">Self-Employed</option>
                      <option value="retired">Retired</option>
                      <option value="student">Student</option>
                      <option value="unemployed">Unemployed</option>
                    </select>
                  </div>

                  {(formData.employmentStatus === 'employed' || formData.employmentStatus === 'self_employed') && (
                    <>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                          Employer Name
                        </label>
                        <input
                          type="text"
                          name="employerName"
                          value={formData.employerName}
                          onChange={handleChange}
                          placeholder="Company Name"
                          className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                          Job Title
                        </label>
                        <input
                          type="text"
                          name="jobTitle"
                          value={formData.jobTitle}
                          onChange={handleChange}
                          placeholder="Software Engineer"
                          className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Annual Income *
                    </label>
                    <select
                      name="annualIncome"
                      value={formData.annualIncome}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                      required
                    >
                      <option value="">Select range</option>
                      <option value="0_25k">$0 - $25,000</option>
                      <option value="25k_50k">$25,000 - $50,000</option>
                      <option value="50k_75k">$50,000 - $75,000</option>
                      <option value="75k_100k">$75,000 - $100,000</option>
                      <option value="100k_plus">$100,000+</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                      Primary Income Source *
                    </label>
                    <select
                      name="incomeSource"
                      value={formData.incomeSource}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-dark-700 dark:text-white"
                      required
                    >
                      <option value="">Select source</option>
                      <option value="employment">Employment</option>
                      <option value="business">Business</option>
                      <option value="investments">Investments</option>
                      <option value="retirement">Retirement</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Document Upload */}
            {currentStep === 5 && (
              <div className="space-y-3 sm:space-y-4">
                <div className="text-center mb-3 sm:mb-6">
                  <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                    Identity Verification
                  </h2>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                    Upload your identification documents
                  </p>
                </div>

                <div className="space-y-3">
                  {[
                    { key: 'driversLicenseFront', title: "Driver's License (Front)", icon: Shield, required: true },
                    { key: 'driversLicenseBack', title: "Driver's License (Back)", icon: Shield, required: true },
                    { key: 'additionalId', title: "Additional ID", icon: FileText, required: false },
                    { key: 'selfie', title: "Selfie Verification", icon: Camera, required: true }
                  ].map((doc) => (
                    <div key={doc.key} className="border-2 border-dashed border-gray-300 dark:border-dark-600 rounded-lg p-3 sm:p-4 hover:border-blue-500 transition-colors">
                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <doc.icon className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-xs sm:text-sm md:text-base text-gray-900 dark:text-white">
                            {doc.title} {doc.required && '*'}
                          </h3>
                          <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400 break-all">
                            {formData.documents[doc.key]
                              ? formData.documents[doc.key].name
                              : 'Upload clear photo or PDF'}
                          </p>
                        </div>
                        <label className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white text-xs sm:text-sm md:text-base rounded-lg hover:bg-blue-700 cursor-pointer flex items-center justify-center gap-2 transition-colors flex-shrink-0">
                          <Upload className="w-4 h-4 sm:w-5 sm:h-5" />
                          Upload
                          <input
                            type="file"
                            accept={doc.key === 'selfie' ? 'image/*' : 'image/*,.pdf'}
                            onChange={(e) => handleFileUpload(e, doc.key)}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex gap-2 sm:gap-3">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-[10px] sm:text-xs md:text-sm text-blue-800 dark:text-blue-300">
                    All documents are encrypted and stored securely. We'll review within 24-48 hours.
                  </p>
                </div>
              </div>
            )}

            {/* Step 6: Review */}
            {currentStep === 6 && (
              <div className="space-y-3 sm:space-y-4">
                <div className="text-center mb-3 sm:mb-6">
                  <h2 className="text-base sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">
                    Review & Submit
                  </h2>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                    Please review your information
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3 sm:p-4 space-y-3 max-h-[300px] sm:max-h-[400px] overflow-y-auto">
                  <div>
                    <h3 className="font-semibold text-xs sm:text-sm text-gray-900 dark:text-white mb-1">Personal</h3>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 break-words">
                      {formData.fullName} • {formData.email}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-xs sm:text-sm text-gray-900 dark:text-white mb-1">Primary Address</h3>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                      {formData.addresses[0].streetAddress}, {formData.addresses[0].city}, {formData.addresses[0].state}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-xs sm:text-sm text-gray-900 dark:text-white mb-1">Employment</h3>
                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 capitalize">
                      {formData.employmentStatus.replace(/_/g, ' ')} • {formData.annualIncome}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-xs sm:text-sm text-gray-900 dark:text-white mb-1">Documents</h3>
                    <div className="flex gap-2 flex-wrap">
                      {formData.documents.driversLicenseFront && (
                        <span className="px-2 py-1 text-[10px] sm:text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">License ✓</span>
                      )}
                      {formData.documents.selfie && (
                        <span className="px-2 py-1 text-[10px] sm:text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">Selfie ✓</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="termsAccepted"
                      checked={formData.termsAccepted}
                      onChange={handleChange}
                      className="mt-0.5 sm:mt-1 flex-shrink-0"
                    />
                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                      I agree to the <a href="#" className="text-blue-600 underline">Terms of Service</a>
                    </span>
                  </label>

                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="privacyAccepted"
                      checked={formData.privacyAccepted}
                      onChange={handleChange}
                      className="mt-0.5 sm:mt-1 flex-shrink-0"
                    />
                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                      I acknowledge the <a href="#" className="text-blue-600 underline">Privacy Policy</a>
                    </span>
                  </label>

                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="esignConsent"
                      checked={formData.esignConsent}
                      onChange={handleChange}
                      className="mt-0.5 sm:mt-1 flex-shrink-0"
                    />
                    <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                      I consent to electronic communications
                    </span>
                  </label>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex gap-2">
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-xs sm:text-sm text-yellow-800 dark:text-yellow-300 mb-1">Important</p>
                    <p className="text-[10px] sm:text-xs text-yellow-700 dark:text-yellow-400">
                      Review within 24-48 hours. You'll receive an email notification.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-4 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 dark:border-dark-700 gap-2 sm:gap-3">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="px-3 sm:px-4 py-2 text-xs sm:text-sm md:text-base border-2 border-gray-300 dark:border-dark-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 sm:gap-2 transition-colors"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
              <span className="hidden xs:inline">Previous</span>
              <span className="xs:hidden">Back</span>
            </button>

            <div className="text-[10px] sm:text-xs md:text-sm text-gray-600 dark:text-gray-400 font-medium">
              {currentStep}/6
            </div>

            {currentStep < 6 ? (
              <button
                onClick={nextStep}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm md:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1 sm:gap-2 transition-colors"
              >
                <span className="hidden xs:inline">Next</span>
                <span className="xs:hidden">Continue</span>
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-3 sm:px-4 py-2 text-xs sm:text-sm md:text-base bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 sm:gap-2 transition-colors"
              >
                {loading ? (
                  <>
                    <div className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span className="hidden xs:inline">Submitting...</span>
                    <span className="xs:hidden">Wait...</span>
                  </>
                ) : (
                  <>
                    Submit
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-3 sm:mt-4 px-2">
          Already have an account?{' '}
          <a href="/login" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 font-medium">
            Sign in
          </a>
        </p>
      </div>
    </div>
  )
}

export default Register